/**
 * Router for @claimshield/marketing (legacy package name).
 *
 * This package serves apply.claimshieldpro.com — driver application,
 * authorization/loss-run signing, /get-quote insurance flow, plus
 * re-exported auth views. It is NOT the public marketing site
 * (that's src/marketing-site/ in Hydra).
 *
 * See src/platform/packages/marketing/CLAUDE.md for context.
 */
import { createRouter, createWebHistory, type RouteLocation, type RouteRecordRaw } from 'vue-router'
import { MarketingLayout, LoginView, ForgotPasswordView, ResetPasswordView, NotFoundView } from '@claimshield/shared'

/**
 * Chunk-load error recovery.
 *
 * When the `frontend-apply` container is rebuilt (10-15x per day), Vite emits
 * new hashed chunk filenames. Users mid-form holding the OLD `index.html` in
 * memory reference the old hashes — fetch returns 404 and the route silently
 * fails to blank. Reload once to pick up the new `index.html`;
 * `DriverApplicationView` localStorage auto-save restores form state.
 *
 * The implementation moved to `@claimshield/shared/router/chunk-reload` — it
 * was duplicated here and in shared's `guards.ts`, and the shared copy sat
 * broken (Chrome-only matching, no loop protection) precisely because this
 * better copy was invisible from there. Re-exported so `main.ts`'s
 * window-level backstop keeps importing from `./router` unchanged.
 */
import {
  isChunkLoadError,
  isChunkLoadErrorMessage,
  attemptChunkReload,
  clearedChunkReloadQuery,
} from '@claimshield/shared/router/chunk-reload'

// Re-exported for main.ts's window-level backstop, which imports from './router'.
export { isChunkLoadErrorMessage, attemptChunkReload }

const routes: RouteRecordRaw[] = [
  // Auth routes (for users who want to log into portal from marketing site)
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { requiresAuth: false, layout: 'auth', title: 'Sign In | ClaimShield' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPasswordView,
    meta: { requiresAuth: false, layout: 'auth', title: 'Forgot Password | ClaimShield' },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPasswordView,
    meta: { requiresAuth: false, layout: 'auth', title: 'Reset Password | ClaimShield' },
  },

  // Driver Application (standalone — no MarketingLayout)
  // DOT-less landing (legacy-form "this application has moved" links): asks
  // for the DOT, generates the direct link, forwards into the wizard. Must
  // be registered before the parametric route below.
  {
    path: '/',
    name: 'apply-landing',
    component: () => import('@views/apply/ApplyLandingView.vue'),
    meta: { requiresAuth: false, title: 'Driver Application | ClaimShield' },
  },
  // Primary route: apply.claimshieldpro.com/:dotNumber/:formType
  {
    path: '/:dotNumber(\\d+)/:formType',
    name: 'driver-application',
    component: () => import('@views/apply/DriverApplicationView.vue'),
    meta: { requiresAuth: false, title: 'Driver Application' },
  },
  // Authorization Form Signing (standalone — no MarketingLayout)
  {
    path: '/sign/:accessToken',
    name: 'authorization-signing',
    component: () => import('@views/sign/AuthorizationSigningView.vue'),
    meta: { requiresAuth: false, title: 'Authorization Forms' },
  },
  // Public SMS consent form — the opt-in Twilio's carrier reviewers can reach.
  // Standalone (no MarketingLayout). Prefill via ?name=&phone=&role=&company=.
  // knowledge/sms-registration/specs/2026-08-26-public-sms-consent-form-design.md
  {
    path: '/sms-consent',
    name: 'sms-consent',
    component: () => import('@views/sms-consent/SmsConsentView.vue'),
    meta: { requiresAuth: false, title: 'SMS Consent | ClaimShield' },
  },

  // Public self-service entry point — prospect creates a submission with no
  // staff involvement. Redirects to /insurance/<token> on success.
  {
    path: '/get-quote',
    name: 'get-quote',
    component: () => import('@views/insurance/GetQuoteView.vue'),
    meta: { requiresAuth: false, title: 'Get a Commercial Auto Quote | ClaimShield' },
  },

  // Insurance Intake Form — V2 is now the default (12-step prefilled
  // wizard). V1 stays reachable at /insurance/:token/v1 as a rollback
  // path. /insurance/:token/v2 stays as an explicit alias for the
  // outstanding email links that already include it.
  {
    path: '/insurance/:token',
    name: 'insurance-intake',
    component: () => import('@views/insurance/InsuranceIntakeWizardV2.vue'),
    meta: { requiresAuth: false, title: 'Commercial Auto Insurance Application' },
    // ?v=1 query param falls back to the old 11-step form during the cutover.
    beforeEnter: (to) => {
      if (to.query.v === '1') {
        return { name: 'insurance-intake-v1', params: { token: to.params.token } }
      }
      return true
    },
  },

  // Insurance Intake V2 — explicit /v2 alias kept active so outstanding
  // links continue to land on the same component as /insurance/:token.
  {
    path: '/insurance/:token/v2',
    name: 'insurance-intake-v2',
    component: () => import('@views/insurance/InsuranceIntakeWizardV2.vue'),
    meta: { requiresAuth: false, title: 'Commercial Auto Insurance Application' },
  },

  // Insurance Intake V1 — legacy 11-step form. Reachable at /v1 or via
  // the ?v=1 query param for rollback if a V2 issue surfaces in prod.
  {
    path: '/insurance/:token/v1',
    name: 'insurance-intake-v1',
    component: () => import('@views/insurance/InsuranceIntakeView.vue'),
    meta: { requiresAuth: false, title: 'Commercial Auto Insurance Application' },
  },

  // Simplified "TurboTax-style" intake — additive, sits beside V2. Mostly
  // pre-filled, one screen at a time; the prospect confirms rather than types.
  // Shares the same submission + public token endpoints as V2.
  {
    path: '/intake/:token',
    name: 'simple-intake',
    component: () => import('@views/insurance/SimpleIntakeWizard.vue'),
    meta: { requiresAuth: false, title: 'Get Your Insurance Quote | ClaimShield' },
  },

  // Plan-review wizard — seat-model repricing campaign (Email 2). Public,
  // token-gated (no auth headers — the token IS the auth). Confirm drivers →
  // review services → schedule with Zach (Zoho Bookings embed). Design
  // winner: lab/sketches/058-plan-review-wizard/ variant A.
  {
    path: '/plan-review/:token',
    name: 'plan-review',
    component: () => import('@views/plan-review/PlanReviewWizard.vue'),
    meta: { requiresAuth: false, title: 'Your Plan Review | ClaimShield' },
  },

  // Zoho-side links sometimes arrive with a stray /apply prefix (seen
  // 2026-07-16: /apply/intake/{token}?mode=agent from the CRM Intake Cockpit
  // tab). Without this redirect the path falls into the driver-application
  // routes and dies with "Can't load this form". Query (?mode=agent) is
  // preserved.
  {
    path: '/apply/intake/:token',
    redirect: (to: RouteLocation) => ({
      name: 'simple-intake',
      params: { token: to.params.token },
      query: to.query,
    }),
  },

  // Loss Run Authorization Signing (standalone — no MarketingLayout)
  {
    path: '/loss-run-sign/:token',
    name: 'loss-run-signing',
    component: () => import('@views/loss-runs/LossRunSigningView.vue'),
    meta: { requiresAuth: false, title: 'Loss Run Authorization' },
  },

  // Driver employer contact follow-up (standalone — no MarketingLayout)
  {
    path: '/employer-contact-info/:token',
    name: 'employer-contact-followup',
    component: () => import('@views/employer-contact/EmployerContactFollowupView.vue'),
    meta: { requiresAuth: false, title: 'Employer Contact Info | ClaimShield' },
  },

  // DQF Completion Request (standalone — no MarketingLayout)
  // Driver completes whatever Driver Qualification File components are missing.
  {
    path: '/complete-dqf/:token',
    name: 'dqf-completion',
    component: () => import('@views/dqf-completion/DQFCompletionView.vue'),
    meta: { requiresAuth: false, title: 'Complete Your DOT File | ClaimShield' },
  },

  // Client-level Items Needed (standalone — no MarketingLayout)
  // Hiring contact resolves outstanding compliance items across every driver
  // under their carrier in one batch. Sibling to /complete-dqf (per-driver).
  {
    path: '/items-needed/:token',
    name: 'client-items-needed',
    component: () => import('@views/items-needed/ClientItemsNeededView.vue'),
    meta: { requiresAuth: false, title: 'Items Needed | ClaimShield' },
  },

  // Driver contact collection (standalone — no MarketingLayout)
  // Carrier supplies the email address and hire date for active drivers we hold
  // neither for, then sends each driver their authorization packet. Shares the
  // token model with /items-needed above but nothing else — separate store,
  // separate API mount, deliberately not reachable with an items-needed token.
  {
    path: '/driver-contacts/:token',
    name: 'client-driver-contacts',
    component: () => import('@views/driver-contacts/DriverContactsView.vue'),
    meta: { requiresAuth: false, title: 'Driver Contact Details | ClaimShield' },
  },

  // Test application page (standalone — DOT lookup validation, direct URL only).
  // The app is served under VITE_BASE=/apply/, so '/test' is reached at
  // apply.claimshieldpro.com/apply/test. It was '/apply/test', which doubled
  // the prefix to /apply/apply/test.
  {
    path: '/test',
    name: 'test-application',
    component: () => import('@views/apply/TestApplicationView.vue'),
    meta: { requiresAuth: false, title: 'DOT Lookup Test | ClaimShield' },
  },

  // Marketing routes (public)
  {
    path: '/',
    component: MarketingLayout,
    meta: { requiresAuth: false },
    children: [
      // Home
      {
        path: '',
        name: 'dashboard', // Named 'dashboard' for consistency with redirects
        component: () => import('@views/HomeView.vue'),
        meta: { title: 'ClaimShield - Safety & Compliance Services' },
      },

      // About
      {
        path: 'about',
        name: 'about',
        component: () => import('@views/AboutView.vue'),
        meta: { title: 'About Us | ClaimShield' },
      },

      // Transportation division
      {
        path: 'transportation',
        children: [
          {
            path: '',
            name: 'transportation',
            component: () => import('@views/transportation/TransportationView.vue'),
            meta: { title: 'Transportation Services | ClaimShield' },
          },
          {
            path: 'mvr',
            name: 'transportation-mvr',
            component: () => import('@views/transportation/MVRView.vue'),
            meta: { title: 'MVR Services | ClaimShield' },
          },
          {
            path: 'psp',
            name: 'transportation-psp',
            component: () => import('@views/transportation/PSPView.vue'),
            meta: { title: 'PSP Services | ClaimShield' },
          },
          {
            path: 'driver-qualification',
            name: 'transportation-dq',
            component: () => import('@views/transportation/DriverQualificationView.vue'),
            meta: { title: 'Driver Qualification | ClaimShield' },
          },
          {
            path: 'drug-alcohol',
            name: 'transportation-drug-alcohol',
            component: () => import('@views/transportation/DrugAlcoholView.vue'),
            meta: { title: 'Drug & Alcohol Testing | ClaimShield' },
          },
        ],
      },

      // Industrial division
      {
        path: 'industrial',
        children: [
          {
            path: '',
            name: 'industrial',
            component: () => import('@views/industrial/IndustrialView.vue'),
            meta: { title: 'Industrial Safety Services | ClaimShield' },
          },
          {
            path: 'osha-compliance',
            name: 'industrial-osha',
            component: () => import('@views/industrial/OSHAView.vue'),
            meta: { title: 'OSHA Compliance | ClaimShield' },
          },
          {
            path: 'training',
            name: 'industrial-training',
            component: () => import('@views/industrial/TrainingView.vue'),
            meta: { title: 'Safety Training | ClaimShield' },
          },
        ],
      },

      // Contact
      {
        path: 'contact',
        name: 'contact',
        component: () => import('@views/ContactView.vue'),
        meta: { title: 'Contact Us | ClaimShield' },
      },

      // Resources
      {
        path: 'resources',
        name: 'resources',
        component: () => import('@views/ResourcesView.vue'),
        meta: { title: 'Resources | ClaimShield' },
      },
    ],
  },

  // Driver document request — magic-link token upload view (standalone, no auth required)
  {
    path: '/docs/:token',
    name: 'driver-docs-request',
    component: () => import('@views/docs/DriverDocsRequestView.vue'),
    meta: { requiresAuth: false, title: 'Upload Your Documents | ClaimShield' },
  },

  // Hiring-contact form: "We're trying to run the Clearinghouse query for X
  // but don't have an email — please provide one." Public, token-gated.
  {
    path: '/provide-driver-email/:token',
    name: 'provide-driver-email',
    component: () => import('@views/docs/ProvideDriverEmailView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Provide Driver Email | ClaimShield',
    },
  },

  // 404 catch-all
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: { requiresAuth: false, title: 'Not Found | ClaimShield' },
  },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// Set page title on navigation
router.beforeEach((to) => {
  document.title = (to.meta.title as string) || 'ClaimShield'
  return true
})

// Recover from stale chunk references after a deploy.
router.onError((error, to) => {
  if (isChunkLoadError(error)) {
    attemptChunkReload(to.fullPath)
  }
})

// Recovery succeeded — drop the marker and reset the budget.
router.afterEach((to) => {
  const cleaned = clearedChunkReloadQuery(to.query)
  if (cleaned) {
    void router.replace({ path: to.path, query: cleaned, hash: to.hash })
  }
})
